import { Toggle } from '@fluentui/react/lib/Toggle';
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { getActivityFeedSettingsStore } from '../store/notificationSettingStore';
import {
    emailMentionSetting,
    likeSetting,
    documentMentionSetting,
    travelSetting,
    deliverySetting,
    onString,
    offString,
    notificationSettingTitle,
    notificationSettingDescription,
    taskNotificationInsightSetting,
    followUpNotificationInsightSetting,
    outOfRoutineNotificationSetting,
    realTimeFlightUpdateNotificationSetting,
    backButtonTitleString,
} from './ActivityFeedSettings.locstring.json';
import loc from 'owa-localize';
import { changeActivityFeedSettingValue } from '../actions/changeActivityFeedSettingValue';
import { ActivityFeedSettingIds } from '../service/ActivityFeedSettingType';
import { isFeatureEnabled } from 'owa-feature-flags';
import { IconButton } from '@fluentui/react/lib/Button';
import saveActivityFeedSettings from '../utils/saveActivityFeedSettings';

const styles = require('./ActivityFeedSettings.scss');

interface SettingsToggle {
    iconName: ControlIcons;
    label: string;
    settingType: ActivityFeedSettingIds;
    defaultValue: boolean;
    isSettingVisible: boolean;
}

interface ActivityFeedSettingProps {
    isOpx: boolean;
}

function makeChangeCallback(settingName: ActivityFeedSettingIds) {
    return function (event: React.MouseEvent<HTMLElement>, checked?: boolean) {
        changeActivityFeedSettingValue(settingName, checked);
    };
}

export default observer(function ActivityFeedSettings(props: ActivityFeedSettingProps) {
    const settings: SettingsToggle[] = [
        {
            iconName: ControlIcons.Accounts,
            label: emailMentionSetting,
            settingType: ActivityFeedSettingIds.emailMentionNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.emailMentionNotification
            ),
            isSettingVisible: true,
        },
        {
            iconName: ControlIcons.Like,
            label: likeSetting,
            settingType: ActivityFeedSettingIds.likeNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.likeNotification
            ),
            isSettingVisible: !isFeatureEnabled('rp-reactions'),
        },
        {
            iconName: ControlIcons.Accounts,
            label: documentMentionSetting,
            settingType: ActivityFeedSettingIds.documentMentionNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.documentMentionNotification
            ),
            isSettingVisible: true,
        },
        {
            iconName: ControlIcons.Airplane,
            label: travelSetting,
            settingType: ActivityFeedSettingIds.flightNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.flightNotification
            ),
            isSettingVisible: true,
        },
        {
            iconName: ControlIcons.DeliveryTruck,
            label: deliverySetting,
            settingType: ActivityFeedSettingIds.parcelNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.parcelNotification
            ),
            isSettingVisible: true,
        },
        {
            iconName: ControlIcons.AnalyticsLogo,
            label: taskNotificationInsightSetting,
            settingType: ActivityFeedSettingIds.MyAnalyticsTaskNotificationInsight,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.MyAnalyticsTaskNotificationInsight
            ),
            isSettingVisible: isFeatureEnabled('auth-activityFeedRenderMyAnalytics'),
        },
        {
            iconName: ControlIcons.AnalyticsLogo,
            label: followUpNotificationInsightSetting,
            settingType: ActivityFeedSettingIds.MyAnalyticsFollowUpNotificationInsight,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.MyAnalyticsFollowUpNotificationInsight
            ),
            isSettingVisible: isFeatureEnabled('auth-activityFeedRenderMyAnalytics'),
        },
        {
            iconName: ControlIcons.Calendar,
            label: outOfRoutineNotificationSetting,
            settingType: ActivityFeedSettingIds.OutOfRoutineNotification,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.OutOfRoutineNotification
            ),
            isSettingVisible: isFeatureEnabled('auth-activityFeedRenderOutOfRoutine'),
        },
        {
            iconName: ControlIcons.Warning,
            label: realTimeFlightUpdateNotificationSetting,
            settingType: ActivityFeedSettingIds.RealTimeFlightUpdate,
            defaultValue: getActivityFeedSettingsStore().get(
                ActivityFeedSettingIds.RealTimeFlightUpdate
            ),
            isSettingVisible: isFeatureEnabled('auth-activityFeedRenderRealTimeFlight'),
        },
    ];
    let backButton = props.isOpx ? (
        <>
            <IconButton
                className={styles.opxBackButton}
                iconProps={{ iconName: ControlIcons.Back }}
                title={loc(backButtonTitleString)}
                ariaLabel={loc(backButtonTitleString)}
                onClick={saveActivityFeedSettings}
            />
            <br />
        </>
    ) : null;
    const activitySettingsOpxStyle = props.isOpx ? styles.activityFeedSettingsOpx : undefined;
    return (
        <>
            <h3>
                {backButton}
                {loc(notificationSettingTitle)}
            </h3>
            <div className={activitySettingsOpxStyle}>
                <div className={styles.descriptionText}>{loc(notificationSettingDescription)}</div>
                {settings
                    .filter(setting => setting.isSettingVisible)
                    .map(function (setting) {
                        return (
                            <div key={setting.label} className={styles.settingElement}>
                                <Toggle
                                    key={setting.settingType}
                                    label={
                                        <div className={styles.settingLabel}>
                                            <Icon
                                                className={styles.settingIcon}
                                                iconName={setting.iconName}
                                            />
                                            {loc(setting.label)}
                                        </div>
                                    }
                                    defaultChecked={setting.defaultValue}
                                    inlineLabel={true}
                                    onText={loc(onString)}
                                    offText={loc(offString)}
                                    onChange={makeChangeCallback(setting.settingType)}
                                />
                            </div>
                        );
                    })}
            </div>
        </>
    );
});
